import type { LeadStage } from "@/mocks/leadsData";

type Props = {
  currentStage: LeadStage;
  onMoveToNextStage: () => void;
  onSelectStage?: (stage: string) => void;
  stages?: string[];
};

const defaultPipelineStages: LeadStage[] = [
  "New Inquiry",
  "Site Visit Scheduled",
  "Site Visit Done",
  "Proposal Sent",
  "Negotiation",
  "Booking Done",
  "Lost",
];

export function PipelineStageCard({ currentStage, onMoveToNextStage, onSelectStage, stages }: Props) {
  const activeStages = stages && stages.length > 0 ? stages : defaultPipelineStages;
  const currentIdx = activeStages.indexOf(currentStage);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-5">Pipeline Stage</h3>

      <div className="space-y-0">
        {activeStages.map((stage, idx) => {
          const isCompleted = currentIdx !== -1 && idx < currentIdx;
          const isCurrent = stage === currentStage;
          const isLost = stage === "Lost";

          return (
            <div
              key={stage}
              onClick={() => {
                if (!isCurrent && onSelectStage) {
                  onSelectStage(stage);
                }
              }}
              className={`flex items-start gap-3 relative ${
                !isCurrent ? "cursor-pointer group hover:opacity-80 transition-opacity" : ""
              }`}
            >
              {idx < activeStages.length - 1 && (
                <div
                  className={`absolute left-[11px] top-7 w-0.5 h-8 ${
                    isCompleted ? "bg-green-400" : "bg-gray-200"
                  }`}
                />
              )}
              <div
                className={`w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isLost && isCurrent
                    ? "bg-red-500"
                    : isCompleted
                    ? "bg-green-500"
                    : isCurrent
                    ? "bg-indigo-600"
                    : isLost
                    ? "bg-red-100"
                    : "bg-gray-200"
                }`}
              >
                {isCompleted ? (
                  <i className="ri-check-line text-white text-xs" />
                ) : isCurrent ? (
                  <div className="w-2 h-2 rounded-full bg-white" />
                ) : isLost ? (
                  <i className="ri-close-line text-red-400 text-xs" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                )}
              </div>
              <div className={`pb-4 ${idx === activeStages.length - 1 ? "pb-0" : ""}`}>
                <p
                  className={`text-sm font-medium ${
                    isLost && isCurrent
                      ? "text-red-600"
                      : isCurrent
                      ? "text-indigo-600 font-semibold"
                      : isCompleted
                      ? "text-gray-500"
                      : "text-gray-400 group-hover:text-gray-700"
                  }`}
                >
                  {stage}
                  {isCurrent && (
                    <span
                      className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isLost ? "bg-red-50 text-red-700" : "bg-indigo-50 text-indigo-700"
                      }`}
                    >
                      Current
                    </span>
                  )}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={onMoveToNextStage}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-arrow-right-line" /> Move to Next Stage
      </button>
    </div>
  );
}